import {LearningPathAPI} from 'api/learning_path';
import {CourseSectionList} from 'components/learning_path/simple_course_section_list';
import {Button} from 'components/system/button';
import {ContentPlaceholder} from 'components/system/content_placeholder';
import {Divider} from 'components/system/divider';
import {Latex} from 'components/system/latex';
import {ProgressBar} from 'components/system/progress_bar';
import {TextContainer} from 'components/system/text_container';
import {useRouter} from 'next/router';
import {useEffect, useMemo, useState} from 'react';
import styles from './main_course_cta.module.css';
import {LearningPathThumbnail} from './main_course_cta_fallback_image';

type Props = {
  courseUUID?: string;
  collapsible?: boolean;
  reload?: boolean;
};

export const MainCourseCTA = ({courseUUID, reload = true, collapsible = false}: Props) => {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState<LearningPathCourse | null>(null);
  const [collapsed, setCollapsed] = useState(collapsible);

  const COURSE_IMAGES_BUCKET = process.env.NEXT_PUBLIC_COURSE_IMAGES_BUCKET;
  const currentSection = useMemo(() => {
    if (course && course.sections) {
      const completed = course.sections
        .filter((section) => section.is_completed)
        .sort((a, b) => {
          return new Date(b.last_viewed_at!).getTime() - new Date(a.last_viewed_at!).getTime();
        });

      if (completed.length === 0) {
        return course?.sections[0];
      }
      return completed[0];
    }
  }, [course]);

  useEffect(() => {
    const loadCourse = async () => {
      if (courseUUID) {
        const rec = await LearningPathAPI.getCourseChapterByUUID(courseUUID);
        setCourse(rec);
      } else {
        const rec = await LearningPathAPI.getCourseChapterRecommendations();
        setCourse(rec);
      }
      setLoading(false);
    };
    if (reload) {
      loadCourse();
    }
  }, [reload]);

  const getProgress = () => {
    if (!course || !course.sections) return 0;
    const progress =
      course.sections.filter((section) => section.is_completed).length / course?.sections.length;
    return progress ?? 0;
  };

  const renderContent = (section: SectionIndexItem) => {
    if (section.sub_description.toLowerCase().indexOf('video') !== -1) {
      return 'Video';
    }
    return (
      <TextContainer>
        <Latex>{section.content}</Latex>
      </TextContainer>
    );
  };

  if (!course || !course.sections) return null;

  if (loading) return <ContentPlaceholder />;

  const remainingSections = course.sections.filter((section) => !section.is_completed);
  return (
    <div className={styles.card}>
      <div className={styles.titleDivHeader}>
        <img
          width={30}
          height={30}
          src={`${COURSE_IMAGES_BUCKET}/learning_paths/${course.learning_path_img}`}
          alt={course.learning_path_title}
        />
        <h3>({course.learning_path_title}) Learning Path</h3>
      </div>

      {currentSection && (
        <>
          {remainingSections.length !== 0 ? (
            <div>
              You only have <strong>{remainingSections.length} lessons</strong> remaining in{' '}
              <strong>{course.title}</strong> course. Currently you are on{' '}
              <strong>{currentSection.title}</strong>.
            </div>
          ) : (
            <div>
              You have finished all sections in <strong>{course.title}</strong> course.
            </div>
          )}
          <ProgressBar progress={getProgress()} color='green' />
          <div className={styles.imageWrapper}>
            <LearningPathThumbnail course={course} currentSection={currentSection} />
            <div className={styles.description}>
              <h3>{currentSection.title}</h3>
              <div className={styles.contentPreview}>{renderContent(currentSection)}</div>
              <Button
                onClick={() => {
                  router.push(
                    `/learning-paths/${course.learning_path_slug}/${course.slug}/${currentSection.slug}`,
                  );
                }}>
                Continue Lesson
              </Button>
            </div>
          </div>
        </>
      )}

      {remainingSections.length !== 0 && (
        <Button
          size='sm'
          type='outline'
          color='greyMedium'
          onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? 'view' : 'hide'} remaining sections in this course
        </Button>
      )}

      {!collapsed && (
        <>
          <Divider size='sm' />
          <CourseSectionList
            courseSlug={course.slug}
            sections={course.sections.filter((section) => !section.is_completed)}
            learningPathSlug={course.learning_path_slug}
          />
        </>
      )}
    </div>
  );
};
